import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { SparkIcon } from '../SparkIcon/SparkIcon';
import './NavigationActionLink.css';

export interface NavigationActionLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Spark icon slug rendered before the label at 24px (e.g. "discord"). */
  iconSlug?: string;
  /** Opens `href` in a new tab with `rel="noopener noreferrer"`. Defaults to `true`. */
  external?: boolean;
  /** Button label. Single line; overflows with an ellipsis. */
  children: ReactNode;
}

/**
 * Labelled action button for the Main Navigation's right-side actions cluster
 * (Figma "Button Main", medium, on the inverse surface). Renders an anchor so it
 * can point at an external destination such as a community invite.
 */
export const NavigationActionLink = forwardRef<HTMLAnchorElement, NavigationActionLinkProps>(
  function NavigationActionLink({ iconSlug, external = true, children, className = '', ...rest }, ref) {
    const classes = ['spark-navigation-action-link', className].filter(Boolean).join(' ');
    return (
      <a
        ref={ref}
        className={classes}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        {...rest}
      >
        {iconSlug ? (
          <span className="spark-navigation-action-link-icon" aria-hidden="true">
            <SparkIcon slug={iconSlug} size="medium" />
          </span>
        ) : null}
        <span className="spark-navigation-action-link-label">{children}</span>
      </a>
    );
  },
);
